// The repositories a person can review: the repository index's store
// (`/gh`; docs/design.md, "Repository index"), and the jump bar's
// completion source. One per page and account (`repoIndex`), read once.
//
// - The listing: every repository an active installation of the App
//   reaches that the viewer can see (install.ts `installedRepositories`),
//   or, where installations cannot be listed (a development token sign-in,
//   403), the viewer's repositories over GraphQL.
// - The order (logic/repos.ts `orderRepos`): this browser's pins (the jump
//   bar's places, `repo:o/r`, one store for both), then the
//   dashboard's saved pull requests waiting on the viewer, then the
//   viewer's recent pushes (`users/{login}/events`), then the rest by last
//   push. A repository only those name is listed too: it may be one the
//   App is not installed on, which its counts then mark read-only.
// - The counts (open pull requests, the viewer's branches without one, the
//   default branch's checks) are read only for the rows a view says are on
//   screen (`see`), COUNTS_BATCH repositories to a request, one alias each.
//
// Read API: `rows` (the whole index, in order), `filter(query, limit)`
// (what the filter field and owner chips keep, best matches first),
// `owners`, `countsOf(key)`.
import { graphqlResult, graphql, GraphQLFailure, rest } from '../api/gh';
import { onIdentityChange } from '../api/identity';
import {
  coverageMayHaveChanged,
  explicitAccess,
  installedRepositories,
  repoCoverage,
  resetCoverage,
  type Coverage,
  type CoverageSignal,
  type InstalledRepo,
  type UnlistedAccount,
} from '../api/install';
import { failureOf, type Failure } from '../api/failure';
import {
  COUNTS_BATCH,
  filterRepos,
  orderRepos,
  ownersOf,
  pushesFrom,
  readCounts,
  readViewerRepos,
  repoCountsQuery,
  repoCountsVars,
  repoKey,
  VIEWER_REPOS_PAGES,
  VIEWER_REPOS_QUERY,
  waitingFrom,
  type CountRead,
  type RepoCounts,
  type RepoEntry,
  type RepoName,
  type RepoQuery,
  type RepoRow,
  type SavedTurns,
} from '../logic/repos';
import { savedDashboardRows } from './dashCache';
import { session } from './session.svelte';
import { places } from '../jump/jump.svelte';
import type { Places } from '../jump/places.svelte';

/** Where the pinned repositories are kept: the jump bar's places (jump/places.svelte.ts), which hear another tab's changes themselves. */
export type RepoPins = Pick<Places, 'pinnedRepos' | 'pinRepo' | 'unpinRepo'>;

/** What the store reads, given by whoever makes it (the app's own by default, `appSources`). */
export interface RepoSources {
  /** The viewer's login. */
  me(): string;
  /** The pinned repositories, read reactively: a pin made in the jump bar, or another tab, reorders the index at once. */
  pins: RepoPins;
  /**
   * The installations' repositories (install.ts `installedRepositories`),
   * each account's handed over as it arrives, or null when the sign-in
   * cannot list installations.
   */
  installed(opts: {
    onInstallations?: (logins: string[]) => void;
    onListed?: (login: string, repos: InstalledRepo[]) => void;
  }): Promise<{ repos: InstalledRepo[]; failed: UnlistedAccount[] } | null>;
  /** The viewer's repositories (the fallback listing). */
  viewerRepos(): Promise<RepoEntry[]>;
  /** The viewer's recent events (GitHub's `users/{login}/events`). */
  events(login: string): Promise<unknown[]>;
  /** The dashboard's saved rows and attention, or null when none are saved. */
  saved(): Promise<SavedTurns | null>;
  /** One batch's counts, by key (logic/repos.ts `CountRead`). */
  counts(repos: readonly RepoName[]): Promise<Map<string, CountRead>>;
  /** The App's coverage of a repository (install.ts `repoCoverage`). */
  coverage(owner: string, name: string, explicit: boolean): Promise<Coverage>;
  /** Forget the page's cached coverage, so the next `coverage` asks GitHub (install.ts `resetCoverage`). */
  resetCoverage?: () => void;
  now?: () => number;
}

/**
 * A row's counts: being read (`was`: what it said before Try again, which
 * it keeps saying meanwhile, so the row keeps its place); read (with the
 * App's coverage); not shown by GitHub; needing the viewer's SSO
 * authorization; refused by GitHub otherwise (in its words); or failed.
 * Only Try again (`retry`) reads a row again once it has answered.
 */
export type CountsState =
  | { status: 'loading'; was?: Settled }
  | { status: 'ready'; counts: RepoCounts; coverage: Coverage }
  | { status: 'missing' }
  | { status: 'sso'; message: string }
  | { status: 'forbidden'; message: string }
  | { status: 'failed'; failure: Failure };

/** A row's counts once its read is done. */
export type Settled = Exclude<CountsState, { status: 'loading' }>;

/** How long the viewer's events are waited for: past it, the index is ordered without its "pushed recently" group. */
export const EVENTS_DEADLINE_MS = 5_000;

/** `p`, or `fallback` once `ms` have passed without it. */
function withDeadline<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    p.then(
      (v) => (clearTimeout(timer), resolve(v)),
      (e: unknown) => (clearTimeout(timer), reject(e)),
    );
  });
}

const toEntry = (r: InstalledRepo): RepoEntry => ({ key: repoKey(r.owner, r.name), id: r.id, owner: r.owner, name: r.name, pushedAt: r.pushedAt });

export class RepoIndex {
  /**
   * The listing's load: not asked yet, under way (nothing to show yet),
   * shown (`pending` may still name accounts being listed), or failed
   * (`failure` says why).
   */
  status: 'idle' | 'loading' | 'ready' | 'failed' = $state('idle');
  failure: Failure | null = $state(null);
  /** Where the listing came from: the App's installations, or the viewer's repositories. */
  source: 'installations' | 'viewer' | null = $state(null);
  /** Accounts whose installation's repositories are still being listed. */
  pending: readonly string[] = $state.raw([]);
  /** Accounts whose installation's repositories could not be listed, and why: the rest are shown. */
  unlisted: readonly { login: string; failure: Failure }[] = $state.raw([]);

  private entries: readonly RepoEntry[] = $state.raw([]);
  private waiting: ReadonlyMap<string, number> = $state.raw(new Map());
  private pushed: ReadonlyMap<string, string> = $state.raw(new Map());
  private ids: ReadonlyMap<string, number> = $state.raw(new Map());
  private names: ReadonlyMap<string, RepoName> = $state.raw(new Map());
  private counts: ReadonlyMap<string, CountsState> = $state.raw(new Map());
  private loading: Promise<void> | null = null;
  /** The latest read's: resolved once its listing is read or fails (`listed`). */
  private shown: Promise<void> | null = null;
  private generation = 0;
  private readonly inflight = new Set<Promise<void>>();
  private coverageAt: number;
  private rechecking: Promise<void> = Promise.resolve();
  private failuresAt: number;

  /** The pinned repositories, in the order pinned, as their pins spell them. */
  private readonly pinned = $derived.by(() => this.src.pins.pinnedRepos().map((r): RepoName & { key: string } => ({ key: repoKey(r.owner, r.repo), owner: r.owner, name: r.repo })));
  /** Pinned keys, in the order pinned. */
  readonly pins: readonly string[] = $derived(this.pinned.map((p) => p.key));

  /** Every repository listed, in the index's order. */
  readonly rows: RepoRow[] = $derived(
    orderRepos(this.entries, {
      pins: this.pins,
      waiting: this.waiting,
      pushed: this.pushed,
      // What the dashboard, the pushes and the counts spell a repository wins over a pin's spelling.
      names: new Map([...this.pinned.map((p): [string, RepoName] => [p.key, { owner: p.owner, name: p.name }]), ...this.names]),
      ids: this.ids,
    }),
  );
  /** The owners of the rows, most repositories first. */
  readonly owners: { login: string; count: number }[] = $derived(ownersOf(this.rows));

  constructor(private readonly src: RepoSources) {
    this.coverageAt = this.failuresAt = this.now();
  }

  private now(): number {
    return (this.src.now ?? Date.now)();
  }

  /** The rows `q` keeps (words in `owner/name`, one owner), best matches first, at most `limit` (logic/repos.ts `filterRepos`). */
  filter(q: RepoQuery, limit = Infinity): RepoRow[] {
    return filterRepos(this.rows, q, limit);
  }

  /** The counts of the repository `key` (lowercased `o/r`), once asked for (`see`). */
  countsOf(key: string): CountsState | undefined {
    return this.counts.get(key);
  }

  isPinned(key: string): boolean {
    return this.pins.includes(key.toLowerCase());
  }

  /**
   * Pin the repository `key` names (last), as its row spells it, or unpin
   * it, in the jump bar's places (over what another tab pinned meanwhile:
   * they read storage afresh). False when the pin was refused, past their
   * cap of pinned repositories.
   */
  togglePin(key: string): boolean {
    const k = key.toLowerCase();
    const row = this.rows.find((r) => r.key === k);
    const [owner, name] = row ? [row.owner, row.name] : k.split('/');
    if (this.isPinned(k)) {
      this.src.pins.unpinRepo({ owner, repo: name });
      return true;
    }
    return this.src.pins.pinRepo({ owner, repo: name });
  }

  /**
   * Resolves once the read under way has listed every account's
   * repositories, or failed: what orders them (the dashboard copy, the
   * events) is not waited for. At once when no read is under way.
   */
  listed(): Promise<void> {
    return this.shown ?? Promise.resolve();
  }

  /** Read the listing and what orders it, once per page (joining a read under way). */
  load(): Promise<void> {
    this.loading ??= this.read();
    return this.loading;
  }

  /** Read the listing again (Try again), keeping what is shown meanwhile. */
  reload(): Promise<void> {
    this.loading = this.read();
    return this.loading;
  }

  private async read() {
    const gen = ++this.generation;
    const live = () => gen === this.generation;
    let show!: () => void;
    this.shown = new Promise<void>((r) => (show = r));
    if (this.status !== 'ready') this.status = 'loading';
    this.failure = null;
    const me = this.src.me();
    const now = this.now();
    // The dashboard and the events only order the listing: each is shown as it arrives, and without them it is still the listing.
    const ordering = Promise.all([
      this.src
        .saved()
        .catch(() => null)
        .then((saved) => {
          if (!live() || !saved) return;
          const turns = waitingFrom(saved, me);
          this.waiting = turns.waiting;
          this.names = new Map([...turns.names, ...this.names]);
        }),
      withDeadline(this.src.events(me), EVENTS_DEADLINE_MS, [])
        .catch(() => [])
        .then((events) => {
          if (!live()) return;
          const pushes = pushesFrom(events, now);
          this.pushed = pushes.pushed;
          this.ids = pushes.ids;
          this.names = new Map([...pushes.names, ...this.names]);
        }),
    ]);
    try {
      await this.listing(live);
    } catch (e) {
      if (!live()) return;
      this.failure = failureOf(e);
      this.status = 'failed';
      this.loading = null;
      return;
    } finally {
      show();
    }
    await ordering;
  }

  private async listing(live: () => boolean) {
    let shown = new Map<string, RepoEntry>();
    const add = (entries: readonly RepoEntry[]) => {
      if (!live()) return;
      for (const e of entries) if (!shown.has(e.key)) shown.set(e.key, e);
      this.entries = [...shown.values()];
      this.status = 'ready';
    };
    const installed = await this.src.installed({
      onInstallations: (logins) => {
        if (!live()) return;
        shown = new Map();
        this.pending = logins;
      },
      onListed: (login, repos) => {
        if (!live()) return;
        this.pending = this.pending.filter((l) => l !== login);
        add(repos.map(toEntry));
      },
    });
    if (!live()) return;
    if (installed) {
      this.source = 'installations';
      this.pending = [];
      this.unlisted = installed.failed.map((f) => ({ login: f.login, failure: failureOf(f.error) }));
      shown = new Map();
      add(installed.repos.map(toEntry));
      return;
    }
    const viewer = await this.src.viewerRepos();
    if (!live()) return;
    this.source = 'viewer';
    this.unlisted = [];
    add(viewer);
  }

  /**
   * Read the counts of the rows `keys` names that were never asked for,
   * COUNTS_BATCH to a request. A row that answered, a failure included, is
   * not asked again here (a view sees a row whenever it is observed, which
   * would ask a failing one without end): only `retry` asks again.
   * Resolves once they are in.
   */
  see(keys: readonly string[]): Promise<void> {
    return this.ask(keys.filter((k) => !this.counts.has(k)));
  }

  /** Read the counts of `keys` again, whatever they said (a row's Try again: after authorizing SSO, say), each saying what it said meanwhile. */
  retry(keys: readonly string[]): Promise<void> {
    return this.ask(keys.filter((k) => this.counts.get(k)?.status !== 'loading'));
  }

  /** The rows whose counts failed (a whole batch fails together when its request does). */
  get failed(): string[] {
    return [...this.counts].filter(([, c]) => c.status === 'failed').map(([k]) => k);
  }

  /** Read every failed row again, together (Try again for all). */
  retryFailed(): Promise<void> {
    return this.retry(this.failed);
  }

  /**
   * The window came back into focus, or back online: read the failed rows
   * again, at most once a minute (the coverage check's throttle,
   * `coverageMayHaveChanged`). Only these events ask again by themselves,
   * never a row being observed, so a row that keeps failing is asked at
   * most once a minute while the person keeps returning to the page.
   */
  failuresSignal(kind: 'focus' | 'online'): Promise<void> {
    const now = this.now();
    if (!coverageMayHaveChanged({ kind: kind === 'online' ? 'reconnect' : 'focus' }, this.failuresAt, now)) return Promise.resolve();
    this.failuresAt = now;
    return this.retryFailed();
  }

  private async ask(keys: readonly string[]): Promise<void> {
    const byKey = new Map(this.rows.map((r) => [r.key, r]));
    const ask = [...new Set(keys)].filter((k) => byKey.has(k)).map((k) => byKey.get(k)!);
    if (!ask.length) return;
    this.setCounts(
      ask.map((r): [string, CountsState] => {
        const was = this.counts.get(r.key);
        return [r.key, was && was.status !== 'loading' ? { status: 'loading', was } : { status: 'loading' }];
      }),
    );
    const batches: RepoRow[][] = [];
    for (let i = 0; i < ask.length; i += COUNTS_BATCH) batches.push(ask.slice(i, i + COUNTS_BATCH));
    const work = Promise.all(batches.map((b) => this.readBatch(b))).then(() => {});
    this.inflight.add(work);
    try {
      await work;
    } finally {
      this.inflight.delete(work);
    }
  }

  /** Resolves once no counts are being read (those `see` asked for, and any it is asked for meanwhile). */
  async idle(): Promise<void> {
    while (this.inflight.size) await Promise.all([...this.inflight]);
  }

  private async readBatch(batch: RepoRow[]) {
    let got: Map<string, CountRead>;
    try {
      got = await this.src.counts(batch.map((r) => ({ owner: r.owner, name: r.name })));
    } catch (e) {
      const failure = failureOf(e);
      return this.setCounts(batch.map((r) => [r.key, { status: 'failed', failure }]));
    }
    const read = await Promise.all(batch.map(async (r): Promise<[string, CountsState]> => [r.key, await this.stateOf(got.get(r.key))]));
    this.setCounts(read);
    // GitHub's spelling, for a repository only a pin (lowercased) named; the rows are worked out again only when one changed.
    const names = new Map(this.names);
    let renamed = false;
    for (const [key, s] of read)
      if (s.status === 'ready') {
        const [owner, name] = s.counts.nameWithOwner.split('/');
        const was = names.get(key);
        if (owner && name && (was?.owner !== owner || was?.name !== name)) {
          names.set(key, { owner, name });
          renamed = true;
        }
      }
    if (renamed) this.names = names;
  }

  private async stateOf(read: CountRead | undefined): Promise<Settled> {
    switch (read?.kind) {
      case 'ready':
        return { status: 'ready', counts: read.counts, coverage: await this.coverageOf(read.counts) };
      case 'sso':
      case 'forbidden':
        return { status: read.kind, message: read.message };
      case 'failed':
        return { status: 'failed', failure: failureOf(new GraphQLFailure(read.errors)) };
      default:
        return { status: 'missing' };
    }
  }

  private coverageOf(c: RepoCounts): Promise<Coverage> {
    const [owner, name] = c.nameWithOwner.split('/');
    return this.src.coverage(owner, name, explicitAccess(c)).catch((): Coverage => 'unknown');
  }

  /**
   * The App's installations may have changed under the page (install.ts
   * `coverageMayHaveChanged`: on returning to it, at most once a minute):
   * ask GitHub again, and work out every counted row's coverage again, so
   * a repository the viewer just installed the App on is no longer
   * read-only. Resolves once that is done (or the one under way, when it
   * is too soon to ask again).
   */
  coverageSignal(signal: CoverageSignal): Promise<void> {
    const now = this.now();
    if (!coverageMayHaveChanged(signal, this.coverageAt, now)) return this.rechecking;
    this.coverageAt = now;
    this.rechecking = this.recheck();
    return this.rechecking;
  }

  private async recheck() {
    this.src.resetCoverage?.();
    const ready = [...this.counts].filter((e): e is [string, Extract<CountsState, { status: 'ready' }>] => e[1].status === 'ready');
    const again = await Promise.all(ready.map(async ([key, s]): Promise<[string, CountsState]> => [key, { ...s, coverage: await this.coverageOf(s.counts) }]));
    // A row read again meanwhile keeps its newer answer.
    this.setCounts(again.filter(([key]) => this.counts.get(key)?.status === 'ready'));
  }

  private setCounts(entries: [string, CountsState][]) {
    const next = new Map(this.counts);
    for (const [k, v] of entries) next.set(k, v);
    this.counts = next;
  }
}

/** The app's own sources: the bridge, the dashboard's saved copy, the page's coverage cache. */
export function appSources(): RepoSources {
  const me = () => session.user?.login ?? '';
  return {
    me,
    pins: places,
    installed: installedRepositories,
    async viewerRepos() {
      const out: RepoEntry[] = [];
      let after: string | null = null;
      for (let i = 0; i < VIEWER_REPOS_PAGES; i++) {
        const page = readViewerRepos(await graphql(VIEWER_REPOS_QUERY, { after }));
        out.push(...page.entries);
        if (!(after = page.next)) break;
      }
      return out;
    },
    // One page: GitHub's events are newest first, and a page of 100 reaches back far enough for "recently".
    events: (login) => rest<unknown[]>(`users/${encodeURIComponent(login)}/events?per_page=100`).then((b) => (Array.isArray(b) ? b : [])),
    saved: savedDashboardRows,
    async counts(repos) {
      // One alias per repository: a hidden, protected or failing one costs only its own row, and says which it is.
      const res = await graphqlResult(repoCountsQuery(repos.length), repoCountsVars(repos), { partial: true });
      return readCounts(res.data, res.errors, repos, me());
    },
    coverage: (owner, name, explicit) => repoCoverage(owner, name, { explicitAccess: explicit }),
    resetCoverage,
  };
}

let shared: RepoIndex | null = null;
onIdentityChange(() => (shared = null));

/** The page's repository index, for the signed-in account (another account gets its own). */
export function repoIndex(): RepoIndex {
  shared ??= new RepoIndex(appSources());
  return shared;
}

/** Whether a row's repository is one the App cannot write to (read-only), from its counts. */
export function readOnlyOf(c: CountsState | undefined): Coverage | null {
  return c?.status === 'ready' && (c.coverage === 'not_installed' || c.coverage === 'no_permission') ? c.coverage : null;
}
